import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { ApiContract } from '../models/framework.model';
import { ConfigService } from './config.service';
import { DealerStateService } from './dealer-state.service';

/** Minimal dealer search row used to resolve STE pricing group (matches shell SearchApiService). */
interface DealerSearchRow {
  dealerName?: string;
  riskDealerPricingGroup?: string;
}

/**
 * Loads dealer pricing-group context via shared API so route guards and auth
 * do not depend on shell header or shell SearchApiService.
 */
@Injectable({ providedIn: 'root' })
export class DealerNavigationContextService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly dealerState = inject(DealerStateService);

  /**
   * Ensures {@link DealerStateService.dealerNavigationContextReady} is set after
   * resolving STE vs non-STE. Safe to call multiple times; skips HTTP when already ready.
   */
  ensureLoaded(dealerNo: number): Observable<void> {
    if (!dealerNo) {
      this.dealerState.setDealerNavigationContextReady(true);
      return of(undefined);
    }

    if (this.dealerState.dealerNavigationContextReady()) {
      return of(undefined);
    }

    this.dealerState.setDealerNavigationContextReady(false);

    const url = `${this.config.sharedApiUrl}/dealer/Search`;
    return this.http
      .get<ApiContract<DealerSearchRow[]>>(url, {
        params: { searchQuery: dealerNo.toString(), take: 10 }
      })
      .pipe(
        tap((res) => {
          if (!res?.isSuccessful || !Array.isArray(res.payload) || res.payload.length === 0) {
            return;
          }
          const isSte = res.payload[0]?.riskDealerPricingGroup === 'STE';
          this.dealerState.setIsSfsPricingGroup(isSte);
        }),
        catchError(() => of(null)),
        finalize(() => this.dealerState.setDealerNavigationContextReady(true)),
        map(() => undefined)
      );
  }
}
